import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Users, BarChart2, Settings, Lock } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useIsMobile } from "@/hooks/use-mobile";
import { Session } from "@supabase/supabase-js";
import { useFeatureLimits } from "@/hooks/useFeatureLimits";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface BottomNavigationProps {
  session: Session | null;
}

const BottomNavigation = ({ session }: BottomNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const { isFeatureEnabled, getFeatureDescription } = useFeatureLimits();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<string>("");

  if (!isMobile || !session || location.pathname === '/') return null;

  const navigation = [
    {
      name: t('dashboard.navigation.stories'),
      icon: BookOpen,
      href: '/dashboard',
    },
    {
      name: t('dashboard.navigation.characters'),
      icon: Users,
      href: '/dashboard/characters',
      featureKey: 'characters_access',
    },
    {
      name: t('dashboard.navigation.stats'),
      icon: BarChart2,
      href: '/dashboard/stats',
      featureKey: 'stats_access',
    },
    {
      name: t('navigation.profile'),
      icon: Settings,
      href: '/dashboard/more',
    },
  ];

  const handleNavigation = (item: typeof navigation[0]) => {
    if (item.featureKey && !isFeatureEnabled(item.featureKey, 'limit')) {
      setSelectedFeature(item.featureKey);
      setIsDialogOpen(true);
      return;
    }
    navigate(item.href);
  };

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-black/50 backdrop-blur-lg border-t border-white/10 z-50">
        <div className="flex justify-around items-center h-16">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const isLocked = item.featureKey && !isFeatureEnabled(item.featureKey, 'limit');
            
            return (
              <button
                key={item.name}
                onClick={() => handleNavigation(item)}
                className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-colors ${
                  isActive ? 'text-[#7E69AB]' : 'text-white hover:text-white/80'
                }`}
              >
                <div className="relative">
                  <item.icon className="w-5 h-5" />
                  {isLocked && (
                    <Lock className="w-4 h-4 absolute -top-1 -right-1 text-white" />
                  )}
                </div>
                <span className="text-xs font-semibold">{item.name}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('settings.subscription.upgradeRequired')}</DialogTitle>
            <DialogDescription>
              {t('settings.subscription.upgradeForAccess')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-black">
            <Lock className="w-5 h-5 text-primary" />
            <span>{t('navigation.basicSubscriptionRequired')}</span>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              onClick={() => {
                setIsDialogOpen(false);
                navigate("/dashboard/more");
              }}
              className="w-full sm:w-auto"
            >
              {t('settings.subscription.upgradeNow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BottomNavigation;
